import { Gift, Eye, Clock } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["giveaways"]) => void;
}

export function GiveawaysTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.giveaways;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  return (
    <Section
      icon={Gift}
      title="Giveaways"
      description="Commands: /giveaway create <duration> <prize>, /giveaway end, /giveaway reroll. Duration examples: 1h30m, 2d, 45m."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          Hosts start giveaways in Discord with the <code className="text-primary">/giveaway</code> group. Set which
          roles may run <code className="text-primary">/giveaway create</code> below; server administrators can always
          host. Optional <strong>log channel</strong> stores finished giveaway posts.
        </p>
      </div>

      <Card className="p-4 bg-muted/30">
        <h4 className="font-semibold mb-2 text-sm">Slash commands (group: /giveaway)</h4>
        <div className="grid sm:grid-cols-1 gap-1.5 text-sm">
          <div className="flex items-baseline gap-2">
            <code className="text-primary font-mono text-xs">/giveaway create</code>
            <span className="text-xs text-muted-foreground">Duration, prize, winner count (see examples below)</span>
          </div>
          <div className="flex items-baseline gap-2">
            <code className="text-primary font-mono text-xs">/giveaway end</code>
            <span className="text-xs text-muted-foreground">End active giveaway by message ID</span>
          </div>
          <div className="flex items-baseline gap-2">
            <code className="text-primary font-mono text-xs">/giveaway reroll</code>
            <span className="text-xs text-muted-foreground">Pick new winner(s) from entrants</span>
          </div>
        </div>
      </Card>

      <Field
        label="Log channel (optional)"
        hint="Channel for archive / finished giveaway posts."
      >
        <ChannelPicker
          channels={channels}
          value={cfg.log_channel_id}
          onChange={(v) => update("log_channel_id", v === "__none__" ? "" : v)}
        />
      </Field>

      <Field
        label="Roles that can host giveaways"
        hint="These roles can use /giveaway create. Admins are always included."
      >
        <MultiSelect
          options={roles}
          value={cfg.host_role_ids}
          onChange={(v) => update("host_role_ids", v)}
          prefix="@"
          placeholder="+ add role"
        />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Join method"
          hint="Buttons = classic component; Reactions = emoji add/remove to enter/leave; Sections = Discord components v2 layout with join button."
        >
          <Select
            value={cfg.join_style ?? "buttons"}
            onValueChange={(v) =>
              update(
                "join_style",
                v === "reactions"
                  ? "reactions"
                  : v === "sections"
                    ? "sections"
                    : "buttons",
              )
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="buttons">Join button</SelectItem>
              <SelectItem value="reactions">Reactions</SelectItem>
              <SelectItem value="sections">Sections (components v2)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {(cfg.join_style ?? "buttons") === "reactions" && (
          <Field label="Join emoji" hint="Default 🎉 — must match the reaction users click">
            <Input
              value={cfg.reaction_emoji ?? "🎉"}
              onChange={(e) => update("reaction_emoji", e.target.value)}
              placeholder="🎉"
            />
          </Field>
        )}
      </div>

      <div className="rounded-lg border border-border p-4 bg-muted/20 text-sm space-y-1">
        <div className="font-medium">Examples (Discord may show localized option names)</div>
        <code className="block text-xs text-muted-foreground">
          /giveaway create duration:1h prize:Nitro winners:1
        </code>
        <code className="block text-xs text-muted-foreground">
          /giveaway end message_id:123456789012345678
        </code>
        <code className="block text-xs text-muted-foreground">
          /giveaway reroll message_id:123456789012345678
        </code>
      </div>

      {/* Live Preview Section */}
      {cfg.enabled && (
        <div className="space-y-3 pt-6 border-t border-border/50">
          <h3 className="text-sm font-semibold text-muted-foreground tracking-wider uppercase flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Discord Live Preview ({(cfg.join_style ?? "buttons") === "sections" ? "Section V2 Style" : (cfg.join_style ?? "buttons") === "reactions" ? "Reactions Style" : "Button Style"})
          </h3>
          <Card className="p-4 bg-[#1e1f22] border-none text-white font-sans max-w-xl shadow-2xl relative overflow-hidden text-left">
            {/* Discord V2 layout */}
            {(cfg.join_style ?? "buttons") === "sections" ? (
              <div className="space-y-3">
                {/* Header block (type: 9) */}
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-1">
                    <h2 className="text-lg font-bold flex items-center gap-2 text-white">
                      🎉 GIVEAWAY: Nitro Premium
                    </h2>
                    <p className="text-xs text-gray-400">Click the button below to join the giveaway!</p>
                  </div>
                  <div className="h-12 w-12 rounded bg-cover bg-center shrink-0 border border-gray-700 shadow-md bg-[#232428] flex items-center justify-center font-bold text-lg text-primary">
                    🎁
                  </div>
                </div>

                {/* Divider spacer (type: 14) */}
                <div className="h-[1px] bg-gray-800 w-full" />

                {/* Details block (type: 10) */}
                <div className="text-sm text-gray-300 space-y-2 leading-relaxed">
                  <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                    <div className="bg-[#111214] p-2 rounded border border-gray-800/40">
                      <span className="text-gray-500 block">Prize</span>
                      <span className="font-semibold text-emerald-400">Nitro Premium</span>
                    </div>
                    <div className="bg-[#111214] p-2 rounded border border-gray-800/40">
                      <span className="text-gray-500 block">Winners</span>
                      <span className="font-semibold text-amber-400">1 Winner</span>
                    </div>
                    <div className="bg-[#111214] p-2 rounded border border-gray-800/40 col-span-2">
                      <span className="text-gray-500 block">Ends In</span>
                      <span className="font-semibold text-amber-400 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        In 2 hours (Today at &lt;t:relative&gt;)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Button row (type: 1) */}
                <div className="pt-2 flex gap-2">
                  <Button size="sm" className="bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold text-xs px-4 py-2 h-auto flex items-center gap-1 rounded border-none">
                    🎉 0 Participants
                  </Button>
                </div>
              </div>
            ) : (cfg.join_style ?? "buttons") === "reactions" ? (
              // Reactions mode
              <div className="space-y-3">
                <div className="border-l-4 border-emerald-500 pl-3 py-1 space-y-1">
                  <h4 className="font-bold text-white text-base">🎉 GIVEAWAY: Nitro Premium</h4>
                  <p className="text-sm text-gray-300">
                    Winners: **1**<br />
                    Ends: **In 2 hours** (&lt;t:relative&gt;)<br />
                    Hosted by: <strong className="text-white">@Host</strong>
                  </p>
                  <p className="text-xs text-gray-400 pt-2">
                    React with {cfg.reaction_emoji ?? "🎉"} to participate!
                  </p>
                </div>
                {/* Emoji reactions row */}
                <div className="flex gap-2 pt-2">
                  <div className="bg-[#2b2d31] hover:bg-[#35373c] border border-transparent rounded px-2.5 py-1 text-sm font-semibold flex items-center gap-1.5 cursor-pointer shadow-sm">
                    <span>{cfg.reaction_emoji ?? "🎉"}</span>
                    <span className="text-[#dbdee1] text-xs font-bold">12</span>
                  </div>
                </div>
              </div>
            ) : (
              // Buttons mode
              <div className="space-y-3">
                <div className="border-l-4 border-emerald-500 pl-3 py-1 space-y-1">
                  <h4 className="font-bold text-white text-base">🎉 GIVEAWAY: Nitro Premium</h4>
                  <p className="text-sm text-gray-300">
                    Winners: **1**<br />
                    Ends: **In 2 hours** (&lt;t:relative&gt;)<br />
                    Hosted by: <strong className="text-white">@Host</strong>
                  </p>
                  <p className="text-xs text-gray-400 pt-2">
                    Click the button below to participate!
                  </p>
                </div>
                {/* Button row */}
                <div className="pt-2 flex gap-2">
                  <Button size="sm" className="bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold text-xs px-4 py-2 h-auto flex items-center gap-1 rounded border-none">
                    🎉 0 Participants
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </Section>
  );
}
